/*
 * Lunvelis Partners · segunda tanda de recursos de interacción (implementaciones propias, sin dependencias).
 * Patrones de referencia consultados; no se copió código de ninguna biblioteca:
 *   - Magic Card / Card Spotlight (Magic UI, Aceternity UI): foco de luz y borde que siguen al puntero + inclinación leve.
 *   - Placeholders rotativos (Aceternity «Placeholders and Vanish Input», Magic UI «Word Rotate»): la barra de búsqueda sugiere acciones.
 *   - Scroll Progress circular + botón (Magic UI, shadcn/ui): volver arriba con anillo de avance.
 *   - Decrypted / Scramble Text (React Bits): las etiquetas de sección se descifran al aparecer.
 *   - Tooltip (Radix UI, React Aria): retraso inicial, sin retraso al pasar a otro, Escape lo cierra, role="tooltip".
 *   - Shimmer Button (Magic UI): destello al pasar sobre el botón principal.
 *   - Dot Pattern / Dot Grid (Magic UI, React Bits): rejilla de puntos que se ilumina cerca del puntero en las cabeceras.
 *   - Parallax con scroll (Motion useScroll, Aceternity Parallax): la ilustración de cabecera se desplaza más despacio.
 * Todo es mejora progresiva: sin movimiento reducido o sin puntero fino se simplifica o se omite.
 */
(() => {
  if (window.__lpFx2) return;
  window.__lpFx2 = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];
  const wrapper = () => document.querySelector('.page-wrapper');
  const scrollInfo = () => {
    const box = wrapper();
    const inner = box && box.scrollHeight > box.clientHeight + 4 && /(auto|scroll)/.test(getComputedStyle(box).overflowY);
    if (inner) return { top: box.scrollTop, max: box.scrollHeight - box.clientHeight, to: (y) => box.scrollTo({ top: y, behavior: reduced.matches ? 'auto' : 'smooth' }) };
    const doc = document.documentElement;
    return { top: window.scrollY, max: doc.scrollHeight - innerHeight, to: (y) => window.scrollTo({ top: y, behavior: reduced.matches ? 'auto' : 'smooth' }) };
  };

  /* ---------- Tarjetas: foco de luz, borde brillante e inclinación ---------- */
  const setupCards = () => {
    for (const card of all('a.lp-card, .lp-cards .lp-card, .lp-reason, .lp-start-card')) {
      if (card.dataset.lpxCard !== undefined || card.hasAttribute('data-tilt')) continue;
      card.dataset.lpxCard = '';
      card.setAttribute('data-glass', '');
      card.addEventListener('pointermove', (event) => {
        if (!fine.matches || reduced.matches || event.pointerType === 'touch') return;
        const box = card.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width - 0.5;
        const y = (event.clientY - box.top) / box.height - 0.5;
        card.style.setProperty('--rx', `${(-y * 3.2).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${(x * 4).toFixed(2)}deg`);
      }, { passive: true });
      card.addEventListener('pointerleave', () => { card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg'); });
    }
  };

  /* ---------- Barra de búsqueda con sugerencias que rotan ---------- */
  const setupRotator = () => {
    const phrases = ['Busca páginas, acciones y servicios', 'Prueba: agendar una reunión', 'Prueba: ver planes y precios', 'Prueba: servicios de Lunvelis World', 'Prueba: hablar con soporte'];
    for (const hint of all('.lp-cmdhint')) {
      const text = hint.querySelector('span');
      if (!text || hint.dataset.lpxRot !== undefined) continue;
      hint.dataset.lpxRot = '';
      hint.setAttribute('aria-label', 'Abrir el buscador de Lunvelis');
      text.setAttribute('aria-hidden', 'true');
      if (reduced.matches) continue;
      let i = 0;
      setInterval(() => {
        if (document.hidden || hint.matches(':hover, :focus-visible')) return;
        i = (i + 1) % phrases.length;
        text.classList.add('is-out');
        setTimeout(() => { text.textContent = phrases[i]; text.classList.remove('is-out'); text.classList.add('is-in'); setTimeout(() => text.classList.remove('is-in'), 360); }, 220);
      }, 3600);
    }
  };

  /* ---------- Volver arriba con anillo de avance ---------- */
  const setupTop = () => {
    if (document.querySelector('.lp-top')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lp-top';
    button.hidden = true;
    button.setAttribute('aria-label', 'Volver arriba');
    button.dataset.lpTip = 'Volver arriba';
    button.innerHTML = '<svg class="lp-top__ring" viewBox="0 0 44 44" aria-hidden="true"><circle class="lp-top__track" cx="22" cy="22" r="19"/><circle class="lp-top__bar" cx="22" cy="22" r="19" pathLength="100"/></svg><svg class="lp-top__arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V6M6.5 11.5L12 6l5.5 5.5"/></svg>';
    document.body.append(button);
    const bar = button.querySelector('.lp-top__bar');
    let ticking = false;
    const paint = () => {
      ticking = false;
      const { top, max } = scrollInfo();
      const show = max > 240 && top > Math.min(520, max * 0.4);
      if (show === button.hidden) { button.hidden = !show; if (show) requestAnimationFrame(() => button.classList.add('is-on')); else button.classList.remove('is-on'); }
      bar.style.strokeDashoffset = String(100 - (max > 0 ? Math.min(100, (top / max) * 100) : 0));
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(paint); } };
    addEventListener('scroll', onScroll, { passive: true });
    const box = wrapper();
    if (box) box.addEventListener('scroll', onScroll, { passive: true });
    button.addEventListener('click', () => { scrollInfo().to(0); const main = document.getElementById('MainContent'); if (main) { main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); } });
    paint();
  };

  /* ---------- Etiquetas de sección que se descifran ---------- */
  const setupScramble = () => {
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const run = (el) => {
      const final = el.textContent;
      const start = performance.now();
      const length = 640;
      const step = (now) => {
        const progress = Math.min(1, (now - start) / length);
        const keep = Math.floor(final.length * progress);
        el.textContent = [...final].map((char, index) => (index < keep || /\s|[·.,:;¿?¡!-]/.test(char) ? char : letters[Math.floor(Math.random() * letters.length)])).join('');
        if (progress < 1) requestAnimationFrame(step); else el.textContent = final;
      };
      requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) { if (!entry.isIntersecting) continue; observer.unobserve(entry.target); run(entry.target); }
    }, { threshold: 0.6 });
    for (const el of all('.lp-kicker')) {
      if (el.children.length || el.dataset.lpxScr !== undefined || !el.textContent.trim()) continue;
      el.dataset.lpxScr = '';
      el.setAttribute('aria-label', el.textContent.trim());
      observer.observe(el);
    }
  };

  /* ---------- Tooltips accesibles ---------- */
  const setupTips = () => {
    const tip = document.createElement('div');
    tip.className = 'lp-tooltip';
    tip.id = 'lp-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    document.body.append(tip);
    let current = null;
    let timer = 0;
    let warm = 0;
    const label = (el) => el.dataset.lpTip || el.getAttribute('aria-label') || '';
    const hide = () => {
      clearTimeout(timer);
      if (!current) return;
      current.removeAttribute('aria-describedby');
      current = null;
      tip.classList.remove('is-on');
      warm = performance.now();
      setTimeout(() => { if (!current) tip.hidden = true; }, 140);
    };
    const show = (el) => {
      const text = label(el);
      if (!text) return;
      current = el;
      tip.textContent = text;
      tip.hidden = false;
      const box = el.getBoundingClientRect();
      const width = tip.offsetWidth;
      const below = box.top < 56;
      const left = Math.max(8, Math.min(innerWidth - width - 8, box.left + box.width / 2 - width / 2));
      tip.style.left = `${left}px`;
      tip.style.top = `${below ? box.bottom + 10 : box.top - tip.offsetHeight - 10}px`;
      tip.style.setProperty('--origin', below ? 'top' : 'bottom');
      el.setAttribute('aria-describedby', 'lp-tooltip');
      requestAnimationFrame(() => tip.classList.add('is-on'));
    };
    const target = (event) => event.target.closest && event.target.closest('.header-actions__action, .account-button, [data-lp-tip], [data-lp-sound-toggle]');
    document.addEventListener('pointerenter', (event) => {
      if (!fine.matches) return;
      const el = target(event);
      if (!el || el === current) return;
      clearTimeout(timer);
      const instant = performance.now() - warm < 400;
      timer = setTimeout(() => show(el), instant ? 0 : 450);
    }, true);
    document.addEventListener('pointerleave', (event) => { if (target(event)) hide(); }, true);
    document.addEventListener('focusin', (event) => { const el = target(event); if (el && event.target.matches(':focus-visible')) show(el); });
    document.addEventListener('focusout', (event) => { if (target(event)) hide(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') hide(); });
    document.addEventListener('pointerdown', hide, true);
    addEventListener('scroll', hide, { passive: true, capture: true });
    for (const el of all('.header-actions__action')) {
      if (/busc/i.test(el.getAttribute('aria-label') || '')) el.dataset.lpTip = 'Buscar · Ctrl K';
    }
  };

  /* ---------- Destello en el botón principal ---------- */
  const setupShine = () => {
    for (const button of all('.lp-btn--dark-glass')) {
      if (button.querySelector('.lp-btn__shine')) continue;
      const shine = document.createElement('i');
      shine.className = 'lp-btn__shine';
      shine.setAttribute('aria-hidden', 'true');
      button.append(shine);
    }
  };

  /* ---------- Rejilla de puntos en las cabeceras internas + parallax ---------- */
  const setupHeroes = () => {
    for (const hero of all('.lp-page__hero')) {
      if (hero.querySelector('.lp-dots')) continue;
      const dots = document.createElement('div');
      dots.className = 'lp-dots';
      dots.setAttribute('aria-hidden', 'true');
      hero.prepend(dots);
      hero.addEventListener('pointermove', (event) => {
        if (!fine.matches || event.pointerType === 'touch') return;
        const box = hero.getBoundingClientRect();
        dots.style.setProperty('--hx', `${event.clientX - box.left}px`);
        dots.style.setProperty('--hy', `${event.clientY - box.top}px`);
        dots.classList.add('is-on');
      }, { passive: true });
      hero.addEventListener('pointerleave', () => dots.classList.remove('is-on'));
    }
    const scene = document.querySelector('.lp-page__scene');
    if (scene && !reduced.matches) {
      let ticking = false;
      const paint = () => { ticking = false; scene.style.translate = `0 ${(-Math.min(scrollInfo().top, 420) * 0.07).toFixed(1)}px`; };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(paint); } };
      addEventListener('scroll', onScroll, { passive: true });
      const box = wrapper();
      if (box) box.addEventListener('scroll', onScroll, { passive: true });
    }
  };

  const boot = () => {
    setupCards(); setupRotator(); setupTop(); setupScramble(); setupTips(); setupShine(); setupHeroes();
    new MutationObserver(() => { setupCards(); setupShine(); }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
